import {
  Inject,
  ModuleWithProviders,
  NgModule,
  Optional,
  Provider,
} from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import {
  TRANSLOCO_LOADER,
  TRANSLOCO_CONFIG,
  translocoConfig,
  TranslocoModule,
  TRANSLOCO_SCOPE,
  TranslocoScope,
  TranslocoConfig,
} from '@ngneat/transloco';
import { TranslationLoader, ScopeRegistry } from './services';
import {
  i18nConfig,
  ScopeConfig,
  ScopeRegistryType,
  SCOPE_REGISTRY,
  RESOLVER_REGISTRY,
} from './types';
import { TranspileParserPipe } from './pipes/transpile-parser.pipe';
import { TranslateFallbackPipe } from './pipes/translate-fallback.pipe';

const defaultConfig: Partial<TranslocoConfig> = {
  availableLangs: ['en'],
  defaultLang: 'en',
  reRenderOnLangChange: false,
};

@NgModule({
  imports: [HttpClientModule],
  declarations: [TranspileParserPipe, TranslateFallbackPipe],
  exports: [TranslocoModule, TranspileParserPipe, TranslateFallbackPipe],
})
export class i18nModule {
  constructor(
    scopeRegistry: ScopeRegistry,
    @Optional()
    @Inject(SCOPE_REGISTRY)
    config: ScopeRegistryType | ScopeRegistryType[]
  ) {
    if (config != null) {
      scopeRegistry.register(config);
    }
  }

  static forRoot(config?: i18nConfig): ModuleWithProviders<i18nModule> {
    return {
      ngModule: i18nModule,
      providers: [
        {
          provide: TRANSLOCO_CONFIG,
          useValue: translocoConfig({
            ...defaultConfig,
            ...config,
          }),
        },
        {
          provide: TRANSLOCO_LOADER,
          useClass: TranslationLoader,
        },
        {
          provide: RESOLVER_REGISTRY,
          useValue: config?.resolvers ?? undefined,
        },
      ],
    };
  }

  static forChild(
    config?: ScopeConfig | ScopeConfig[]
  ): ModuleWithProviders<i18nModule> {
    let translocoScope: Provider;

    if (Array.isArray(config)) {
      translocoScope = config.map(createTranslationScope);
    } else if (config) {
      translocoScope = [createTranslationScope(config)];
    } else {
      translocoScope = [];
    }

    const scopeRegistry = config
      ? [
          {
            provide: SCOPE_REGISTRY,
            useValue: config as ScopeRegistryType,
            multi: true,
          },
        ]
      : [];

    return {
      ngModule: i18nModule,
      providers: [translocoScope, scopeRegistry],
    };
  }
}

function createTranslationScope(config: ScopeConfig): Provider {
  return {
    provide: TRANSLOCO_SCOPE,
    useValue: {
      scope: config.scope,
      alias: config.alias,
    } as TranslocoScope,
    multi: true,
  };
}
